/**
 * Curated icon map. Re-exports lucide-react under stable names so swapping
 * the icon library later means touching ONLY this file.
 *
 * Usage:
 *   <Icon name="bottle" size={20} className="text-candle" />
 */
import { forwardRef, type SVGProps } from 'react';
import {
  Wine,
  WineOff,
  Grape,
  Search,
  Plus,
  Trash2,
  Pencil,
  Check,
  X,
  Star,
  Calendar,
  Clock,
  Filter,
  ArrowUpDown,
  LayoutGrid,
  List,
  Move,
  GripVertical,
  Sparkles,
  Camera,
  QrCode,
  ScanLine,
  ScanBarcode,
  Printer,
  Upload,
  Download,
  Settings,
  User,
  Users,
  LogOut,
  LogIn,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  Map,
  MapPin,
  Thermometer,
  UtensilsCrossed,
  ChefHat,
  Moon,
  Sun,
  Loader2,
  AlertTriangle,
  Info,
  CircleCheck,
  CircleX,
  Heart,
  Bookmark,
  Boxes,
  PackagePlus,
  Container,
  Sliders,
  Bell,
  Globe,
  Eye,
  EyeOff,
  Mail,
  Lock,
  Home,
  Menu,
  ImageIcon,
  Tag,
  Archive,
  PartyPopper,
  Flame,
  Snowflake,
  Beef,
  Fish,
  EggFried,
  Cookie,
  Cherry,
  Refrigerator,
  ListFilter,
  ArrowRight,
  ArrowUpRight,
  ExternalLink,
  Copy,
  RefreshCw,
  ReceiptText,
  Beer,
  Martini,
  TrendingUp,
  TrendingDown,
  Truck,
  HelpCircle,
  Folder,
  FolderOpen,
  DoorOpen,
  Minus,
} from 'lucide-react';

/**
 * Custom wine-rack glyph: 3x3 grid with bottle holes — much more iconic than
 * lucide's `Boxes`. Accepts the same `size`/`strokeWidth` props as lucide-react
 * so it slots into iconMap transparently.
 */
const RackIcon = forwardRef<
  SVGSVGElement,
  SVGProps<SVGSVGElement> & { size?: number; strokeWidth?: number }
>(({ size = 18, strokeWidth = 1.6, ...rest }, ref) => (
  <svg
    ref={ref}
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...rest}
  >
    <rect x="3" y="3" width="18" height="18" rx="1.5" />
    <line x1="3" y1="9" x2="21" y2="9" />
    <line x1="3" y1="15" x2="21" y2="15" />
    <line x1="9" y1="3" x2="9" y2="21" />
    <line x1="15" y1="3" x2="15" y2="21" />
    <circle cx="6" cy="6" r="1.1" fill="currentColor" />
    <circle cx="12" cy="6" r="1.1" fill="currentColor" />
    <circle cx="18" cy="6" r="1.1" fill="currentColor" />
    <circle cx="6" cy="12" r="1.1" fill="currentColor" />
    <circle cx="12" cy="12" r="1.1" fill="currentColor" />
    <circle cx="18" cy="12" r="1.1" fill="currentColor" />
    <circle cx="6" cy="18" r="1.1" fill="currentColor" />
    <circle cx="12" cy="18" r="1.1" fill="currentColor" />
    <circle cx="18" cy="18" r="1.1" fill="currentColor" />
  </svg>
));
RackIcon.displayName = 'RackIcon';

type IconComponent = React.ComponentType<{ size?: number; strokeWidth?: number; className?: string }>;

export const iconMap = {
  bottle: Wine,
  'bottle-empty': WineOff,
  grape: Grape,
  search: Search,
  plus: Plus,
  trash: Trash2,
  edit: Pencil,
  check: Check,
  x: X,
  star: Star,
  calendar: Calendar,
  clock: Clock,
  filter: Filter,
  'filter-list': ListFilter,
  sort: ArrowUpDown,
  grid: LayoutGrid,
  list: List,
  drag: GripVertical,
  move: Move,
  sparkles: Sparkles,
  camera: Camera,
  qr: QrCode,
  scan: ScanLine,
  barcode: ScanBarcode,
  print: Printer,
  upload: Upload,
  download: Download,
  settings: Settings,
  user: User,
  users: Users,
  logout: LogOut,
  login: LogIn,
  'chevron-right': ChevronRight,
  'chevron-left': ChevronLeft,
  'chevron-down': ChevronDown,
  'chevron-up': ChevronUp,
  region: Map,
  'map-pin': MapPin,
  thermometer: Thermometer,
  utensils: UtensilsCrossed,
  'chef-hat': ChefHat,
  moon: Moon,
  sun: Sun,
  spinner: Loader2,
  warning: AlertTriangle,
  info: Info,
  'check-circle': CircleCheck,
  'x-circle': CircleX,
  heart: Heart,
  bookmark: Bookmark,
  rack: RackIcon,
  'rack-stack': Boxes,
  'add-bottle': PackagePlus,
  container: Container,
  sliders: Sliders,
  bell: Bell,
  globe: Globe,
  eye: Eye,
  'eye-off': EyeOff,
  mail: Mail,
  lock: Lock,
  home: Home,
  menu: Menu,
  image: ImageIcon,
  tag: Tag,
  archive: Archive,
  party: PartyPopper,
  flame: Flame,
  snow: Snowflake,
  beef: Beef,
  fish: Fish,
  egg: EggFried,
  cookie: Cookie,
  cherry: Cherry,
  fridge: Refrigerator,
  'arrow-right': ArrowRight,
  'arrow-up-right': ArrowUpRight,
  external: ExternalLink,
  copy: Copy,
  refresh: RefreshCw,
  receipt: ReceiptText,
  beer: Beer,
  spirits: Martini,
  'trend-up': TrendingUp,
  'trend-down': TrendingDown,
  truck: Truck,
  'help-circle': HelpCircle,
  folder: Folder,
  'folder-open': FolderOpen,
  door: DoorOpen,
  minus: Minus,
} as const satisfies Record<string, IconComponent>;

export type IconName = keyof typeof iconMap;

interface IconProps extends Omit<React.SVGProps<SVGSVGElement>, 'name'> {
  name: IconName;
  size?: number;
  strokeWidth?: number;
}

export function Icon({ name, size = 18, strokeWidth = 1.7, className, ...rest }: IconProps) {
  const Component = iconMap[name];
  return <Component size={size} strokeWidth={strokeWidth} className={className} {...rest} />;
}
